import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft,
  Clock,
  MapPin,
  Search,
  Plus,
  Minus,
  ShoppingBag,
  X,
  Info,
  Tag,
} from "lucide-react";
import { LOCATIONS, BRAND } from "../data/site";
import { useCart } from "../context/CartContext";
import { FoodTypeMark } from "../components/order/FoodTypeMark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TYPE_FILTERS = [
  { id: "veg", label: "Veg", color: "#2E7D32" },
  { id: "non-veg", label: "Non Veg", color: "#B91C1C" },
  { id: "egg", label: "Egg", color: "#D4A24E" },
];

export default function OrderPage() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCat, setActiveCat] = useState(null);
  const [filters, setFilters] = useState(new Set()); // type filters
  const [search, setSearch] = useState("");
  const sectionRefs = useRef({});

  const { addItem, decrementItem, getQty, count, subtotal, storeId: cartStore } =
    useCart();

  const store = useMemo(
    () => LOCATIONS.find((l) => l.id === storeId) || LOCATIONS[0],
    [storeId]
  );

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    axios
      .get(`${API}/order/menu`)
      .then((res) => {
        if (!mounted) return;
        const cats = res.data.categories || [];
        setCategories(cats);
        if (cats.length) setActiveCat(cats[0].id);
      })
      .catch((e) => console.error(e))
      .finally(() => mounted && setLoading(false));
    return () => {
      mounted = false;
    };
  }, []);

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    return categories
      .map((c) => ({
        ...c,
        items: c.items.filter((it) => {
          if (filters.size > 0 && !filters.has(it.type)) return false;
          if (q) {
            const hay = `${it.name} ${it.description}`.toLowerCase();
            if (!hay.includes(q)) return false;
          }
          return true;
        }),
      }))
      .filter((c) => c.items.length > 0);
  }, [categories, filters, search]);

  const toggleFilter = (id) => {
    setFilters((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const scrollToCat = (id) => {
    setActiveCat(id);
    const el = sectionRefs.current[id];
    if (el)
      window.scrollTo({
        top: el.offsetTop - 90,
        behavior: "smooth",
      });
  };

  const handleAdd = (item) => {
    addItem(
      { id: item.id, name: item.name, price: item.price, image: item.image, type: item.type },
      store?.id
    );
  };

  // Switch-store warning when adding while cart belongs to another store
  const otherStoreInCart =
    cartStore && cartStore !== store?.id && count > 0;

  return (
    <div data-testid="order-page" className="bg-[#F7F1E4] min-h-screen pb-32">
      {/* Top bar */}
      <header className="sticky top-0 z-40 bg-[#FAF3E7]/95 backdrop-blur border-b border-[#E8D8C0]">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-16 flex items-center gap-4">
          <Link
            to="/#locations"
            data-testid="order-back-link"
            className="inline-flex items-center gap-1.5 text-cocoa-soft hover:text-cocoa text-sm"
          >
            <ArrowLeft size={16} /> Back
          </Link>
          <div className="flex items-center gap-2 ml-1">
            <img
              src={BRAND.logoTransparent}
              alt=""
              className="h-8 w-8 rounded-full ring-1 ring-[#C8A14B]/60 bg-[#3A1B0E]"
            />
            <span className="font-display text-cocoa">Andhra Biryani Co</span>
          </div>
          <div className="ml-auto hidden md:flex items-center gap-3 text-xs text-cocoa-soft">
            <Clock size={14} className="text-gold" /> 30 min · {store?.area}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 md:px-8 pt-6 grid grid-cols-12 gap-6">
        {/* Sidebar categories */}
        <aside className="hidden md:block md:col-span-3 lg:col-span-3">
          <div className="sticky top-24 bg-white border border-cream-deep rounded-2xl p-4">
            <div className="text-xs uppercase tracking-[0.2em] text-cocoa-soft font-semibold px-2 pb-2">
              Menu
            </div>
            <ul className="space-y-1">
              {categories.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => scrollToCat(c.id)}
                    data-testid={`order-cat-${c.id}`}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm flex items-center justify-between transition-colors ${
                      activeCat === c.id
                        ? "bg-[#FFF4D6] text-cocoa font-semibold border-l-2 border-[#C8A14B]"
                        : "text-cocoa-soft hover:bg-[#FAF3E7]"
                    }`}
                  >
                    <span>{c.name}</span>
                    <span className="text-xs text-cocoa-soft">
                      ({c.items.length})
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </aside>

        {/* Main */}
        <main className="col-span-12 md:col-span-9 lg:col-span-9">
          {/* Store / Offer / Filters */}
          <div className="bg-white border border-cream-deep rounded-2xl p-5 md:p-6">
            <div className="flex flex-wrap items-start gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <h1 className="font-display text-2xl md:text-3xl text-cocoa">
                    {BRAND.name}
                  </h1>
                  <Info size={16} className="text-cocoa-soft" />
                  <span className="ml-2 inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded border border-[#2E7D32] text-[#2E7D32]">
                    Open
                  </span>
                </div>
                <div className="mt-2 flex items-center gap-4 text-xs text-cocoa-soft">
                  <span className="inline-flex items-center gap-1">
                    <Clock size={14} className="text-gold" /> 30 Minutes
                  </span>
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={14} className="text-gold" /> {store?.area}
                  </span>
                </div>
              </div>

              <div className="ml-auto flex items-center gap-3">
                <div className="hidden lg:flex items-center gap-3 bg-[#FFF8E6] border border-[#F0E2B6] rounded-xl px-4 py-2">
                  <Tag size={16} className="text-gold" />
                  <div className="leading-tight">
                    <div className="text-sm font-semibold text-cocoa">
                      Get ₹150 OFF on orders above ₹999
                    </div>
                    <div className="text-[11px] text-cocoa-soft">
                      Use Code FLAT150 · 4 Offers
                    </div>
                  </div>
                </div>
                <div className="border border-cream-deep rounded-xl px-4 py-2 text-center">
                  <div className="text-[10px] uppercase tracking-wider text-cocoa-soft">
                    Delivery
                  </div>
                  <button className="text-sm font-semibold text-cocoa underline decoration-[#C8A14B] underline-offset-2">
                    Change
                  </button>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="mt-5 flex flex-wrap items-center gap-3">
              {TYPE_FILTERS.map((f) => {
                const active = filters.has(f.id);
                return (
                  <button
                    key={f.id}
                    data-testid={`order-filter-${f.id}`}
                    onClick={() => toggleFilter(f.id)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-sm transition-all ${
                      active
                        ? "bg-[#FAF3E7] border-[#C8A14B] text-cocoa"
                        : "border-cream-deep text-cocoa-soft hover:bg-[#FAF3E7]"
                    }`}
                  >
                    <FoodTypeMark type={f.id} />
                    {f.label}
                  </button>
                );
              })}

              <div className="ml-auto relative flex-1 min-w-[200px] max-w-sm">
                <Search
                  size={16}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-cocoa-soft"
                />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  data-testid="order-search-input"
                  placeholder="Search menu"
                  className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-cream-deep text-sm focus:outline-none focus:border-[#C8A14B] focus:ring-2 focus:ring-[#C8A14B]/30"
                />
              </div>
            </div>
          </div>

          {/* Switch store warning */}
          {otherStoreInCart && (
            <div
              data-testid="order-store-switch-warning"
              className="mt-4 bg-[#FFF6E6] border border-[#F0E2B6] rounded-xl px-4 py-3 text-sm text-cocoa flex items-center gap-2"
            >
              <Info size={16} className="text-chilli" />
              You have items from another store in your cart. Adding from this
              store will replace the cart.
            </div>
          )}

          {/* Categories */}
          {loading ? (
            <div className="mt-10 text-center text-cocoa-soft">
              Loading menu…
            </div>
          ) : (
            <div className="mt-6 space-y-12">
              {filteredCategories.map((c) => (
                <section
                  key={c.id}
                  id={`cat-${c.id}`}
                  ref={(el) => (sectionRefs.current[c.id] = el)}
                  data-testid={`order-cat-section-${c.id}`}
                  className="scroll-mt-24"
                >
                  <h2 className="text-center font-display text-xl tracking-[0.18em] uppercase text-cocoa">
                    {c.name}
                  </h2>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                    {c.items.map((item) => {
                      const qty = getQty(item.id);
                      return (
                        <article
                          key={item.id}
                          data-testid={`order-item-${item.id}`}
                          className="bg-white border border-cream-deep rounded-2xl p-5 flex gap-5 shadow-sm hover:shadow-md transition-shadow"
                        >
                          <div className="flex-1 min-w-0">
                            <FoodTypeMark type={item.type} />
                            <h3 className="font-semibold text-cocoa mt-2 leading-snug">
                              {item.name}
                            </h3>
                            <div className="mt-1 text-cocoa font-semibold">
                              ₹{item.price}
                            </div>
                            <p className="mt-2 text-xs text-cocoa-soft leading-relaxed line-clamp-3">
                              {item.description}
                            </p>
                          </div>

                          <div className="relative w-28 sm:w-32 shrink-0">
                            <img
                              src={item.image}
                              alt={item.name}
                              loading="lazy"
                              className="w-full h-32 sm:h-36 object-cover rounded-xl"
                            />
                            {qty === 0 ? (
                              <button
                                onClick={() => handleAdd(item)}
                                data-testid={`order-add-${item.id}`}
                                className="absolute -bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 rounded-full bg-[#C8A14B] text-[#3A1B0E] hover:bg-[#D4A24E] px-4 py-1.5 text-xs font-bold shadow-md transition-colors"
                              >
                                <Plus size={12} /> ADD
                              </button>
                            ) : (
                              <div
                                data-testid={`order-qty-${item.id}`}
                                className="absolute -bottom-3 left-1/2 -translate-x-1/2 inline-flex items-center rounded-full bg-[#C8A14B] text-[#3A1B0E] shadow-md text-xs font-bold"
                              >
                                <button
                                  onClick={() => decrementItem(item.id)}
                                  data-testid={`order-dec-${item.id}`}
                                  className="px-2.5 py-1.5 hover:bg-[#B89240] hover:text-[#FAF3E7] rounded-l-full transition-colors"
                                  aria-label="Decrease"
                                >
                                  <Minus size={12} />
                                </button>
                                <span className="px-2 min-w-[24px] text-center">
                                  {qty}
                                </span>
                                <button
                                  onClick={() => handleAdd(item)}
                                  data-testid={`order-inc-${item.id}`}
                                  className="px-2.5 py-1.5 hover:bg-[#B89240] hover:text-[#FAF3E7] rounded-r-full transition-colors"
                                  aria-label="Increase"
                                >
                                  <Plus size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        </article>
                      );
                    })}
                  </div>
                </section>
              ))}

              {filteredCategories.length === 0 && (
                <div className="text-center text-cocoa-soft py-16">
                  No dishes match your search.
                </div>
              )}
            </div>
          )}
        </main>
      </div>

      {/* Floating cart bar */}
      {count > 0 && (
        <div
          data-testid="cart-floating-bar"
          className="fixed bottom-4 inset-x-4 md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-6 z-50"
        >
          <button
            onClick={() => navigate("/checkout")}
            data-testid="cart-view-btn"
            className="w-full md:w-[520px] flex items-center justify-between gap-4 rounded-full bg-[#3A1B0E] text-[#FAF3E7] px-5 py-3.5 shadow-2xl hover:bg-[#4A2218] transition-colors"
          >
            <div className="flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-[#C8A14B] text-[#3A1B0E]">
                <ShoppingBag size={16} />
              </span>
              <span className="font-semibold text-sm">
                {count} item{count > 1 ? "s" : ""} · ₹{subtotal}
              </span>
            </div>
            <span className="text-sm font-semibold">View Cart →</span>
          </button>
        </div>
      )}
    </div>
  );
}
